import { defaults, filter, indexOf, isEmpty, isEqual } from 'lodash';
import { Observable, of } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';
import { EmptyResponse, IMobizStateService, SerializableObject } from '../../core';

import { MobizSettings } from '../data/mobiz-settings.do';
import { RecentObjectsSettings } from '../data/recent-objects-settings.do';
import { IMobizSettingsService } from '../interfaces/mobiz-settings.interface';
import { MobizSettingsKeyRecentObject } from '../other/mobiz-settings.type';

import { IRecentObjectsService } from './recent-objects.interface';

export abstract class RecentObjectsService<T extends SerializableObject> implements IRecentObjectsService<T> {
  private static maxRecentObjects: number = 10;

  /**
   * Returns the selected organization id.
   */
  protected get organizationId(): string {
    if (!this.state.currentMembership.value) {
      throw new Error('No selected membership');
    }
    return this.state.currentMembership.value.organizationId;
  }

  /**
   * Returns the user id from the selected organization.
   */
  protected get userId(): string {
    if (!this.state.currentUser.value) {
      throw new Error('No selected membership');
    }
    return this.state.currentUser.value.id;
  }

  protected constructor(
    protected settingsService: IMobizSettingsService,
    protected state: IMobizStateService,
  ) {
  }

  public getRecentObjects(): Observable<T[]> {
    let properties: string[];
    return this.getSettings().pipe(
      concatMap((settings: MobizSettings<RecentObjectsSettings>) => {
        properties = settings.value.getValues(this.organizationId);
        // no need to fetch the objects if there are no prior saved properties
        if (isEmpty(properties)) {
          return of([]);
        }
        return this.getObjects(properties);
      }),
      map((unsortedObjects: T[]) => {
        return this.sortObjects(unsortedObjects, properties);
      }),
    );
  }

  public addToRecentObjects(object: T): Observable<EmptyResponse> {
    return this.getSettings().pipe(
      concatMap((settings: MobizSettings<RecentObjectsSettings>) => {
        let properties = settings.value.getValues(this.organizationId);
        const propertyValueToAdd = this.getObjectProperty(object);
        const currentIndex = indexOf(properties, propertyValueToAdd);
        // If object is already in the first position we don't do anything
        if (currentIndex === 0) {
          return of(new EmptyResponse());
        }
        properties = filter(properties, (property: string): boolean => {
          return property !== propertyValueToAdd;
        });
        properties.unshift(propertyValueToAdd);
        properties.slice(0, RecentObjectsService.maxRecentObjects);
        settings.value.setValues(this.organizationId, properties);
        return this.saveSettings(settings);
      }));
  }

  public removeFromRecentObjects(object: T): Observable<EmptyResponse> {
    return this.getSettings().pipe(
      concatMap((settings: MobizSettings<RecentObjectsSettings>) => {
        let properties = settings.value.getValues(this.organizationId);
        const propertyValueToRemove = this.getObjectProperty(object);
        if (indexOf(properties, propertyValueToRemove) === -1) {
          return of(new EmptyResponse());
        }
        properties = filter(properties, (property: string): boolean => {
          return property !== propertyValueToRemove;
        });
        settings.value.setValues(this.organizationId, properties);
        return this.saveSettings(settings);
      }));
  }

  public clearRecentObjects(): Observable<EmptyResponse> {
    return this.getSettings().pipe(
      concatMap((settings: MobizSettings<RecentObjectsSettings>): Observable<EmptyResponse> => {
        const properties: string[] = settings.value.getValues(this.organizationId);
        if (isEmpty(properties)) {
          return of(new EmptyResponse());
        }
        settings.value.setValues(this.organizationId, []);
        return this.saveSettings(settings);
      }));
  }

  public updateRecentObjects(valuesByOrganization: Record<string, string[]>): Observable<EmptyResponse> {
    return this.getSettings().pipe(
      concatMap((settings: MobizSettings<RecentObjectsSettings>): Observable<EmptyResponse> => {
        if (isEqual(settings.value.valuesByOrganization, valuesByOrganization)) {
          return of(new EmptyResponse());
        }
        settings.value.valuesByOrganization = defaults({}, valuesByOrganization, settings.value.valuesByOrganization);
        return this.saveSettings(settings);
      }));
  }

  /**
   * Reads the objects identified by the provided list of properties.
   *
   * @param properties List of properties used to fetch data from the server.
   */
  protected abstract getObjects(properties: string[]): Observable<T[]>;

  /**
   * Return the unique key that will identify the settings.
   */
  protected abstract getKey(): MobizSettingsKeyRecentObject;

  /**
   * Returns a object property to be used when querying the recent objects.
   */
  protected abstract getObjectProperty(object: T): string;

  protected sortObjects(unsortedObjects: T[], sortedProperties: string[]): T[] {
    return unsortedObjects.sort((o1: T, o2: T): number => {
      const o1Index = indexOf(sortedProperties, this.getObjectProperty(o1));
      const o2Index = indexOf(sortedProperties, this.getObjectProperty(o2));
      return o1Index - o2Index;
    });
  }

  private getSettings(): Observable<MobizSettings<RecentObjectsSettings>> {
    const settingsInstance = new MobizSettings<RecentObjectsSettings>(RecentObjectsSettings);
    return this.settingsService.get('user', this.userId, this.getKey(), settingsInstance);
  }

  private saveSettings(settings: MobizSettings<RecentObjectsSettings>): Observable<MobizSettings<RecentObjectsSettings>> {
    if (settings.origin !== 'NotFound') {
      return this.settingsService.update('user', this.userId, this.getKey(), settings);
    }
    return this.settingsService.add('user', this.userId, this.getKey(), settings);
  }
}
